import { DeleteOutlined, EditOutlined, LoginOutlined, LogoutOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Timeline, Typography } from "antd";
import i18n from "i18next";
import type { FC, ReactElement } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { DashboardCardContainer } from "src/app/features/Dashboard/utils/dashboardStyles";
import { SecondaryButton } from "../../../../components/Buttons/components/ActionButton";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../core/router/types/routerTypes";
import type { ChangeLogEntryDto, RecentActivityDto, RevisionType } from "../types/Dashboard";
import { formatRelativeTime, getRevisionTypeColor } from "../utils/formatters";

const { Text, Paragraph } = Typography;

interface RecentActivityTimelineProps {
    activity: RecentActivityDto;
}

const getRevisionIcon = (type: RevisionType): ReactElement => {
    const iconMap: Record<RevisionType, ReactElement> = {
        INSERT: <PlusCircleOutlined />,
        UPDATE: <EditOutlined />,
        DELETE: <DeleteOutlined />,
        LOGIN: <LoginOutlined />,
        LOGOUT: <LogoutOutlined />,
    };

    return iconMap[type] || <EditOutlined />;
};

export const RecentActivityTimeline: FC<RecentActivityTimelineProps> = ({ activity }) => {
    const navigate = useNavigate();
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
    const [ellipsedItems, setEllipsedItems] = useState<Record<number, boolean>>({});

    const toggleExpanded = (id: number) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedItems(newExpanded);
    };

    const handleSeeAll = () => {
        navigate(`${BASE_PATH}/${ResourceName.USER_LOGS}`);
    };

    const handleEllipsis = (id: number, isEllipsed: boolean) => {
        setEllipsedItems((previousState) => {
            if (previousState[id] === isEllipsed) {
                return previousState;
            }

            return {
                ...previousState,
                [id]: isEllipsed,
            };
        });
    };

    const timelineItems = activity.recentChanges.map((change: ChangeLogEntryDto) => {
        const isExpanded = expandedItems.has(change.id);
        const isEllipsed = Boolean(ellipsedItems[change.id]);

        return {
            color: getRevisionTypeColor(change.revisionType as RevisionType),
            dot: getRevisionIcon(change.revisionType as RevisionType),
            children: (
                <div key={change.id}>
                    <div style={{ marginBottom: 4 }}>
                        <Text strong>{change.className}</Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                            • {change.revisionType}
                        </Text>
                    </div>
                    <Paragraph
                        ellipsis={isExpanded ? false : { rows: 2, onEllipsis: (isNowEllipsed) => handleEllipsis(change.id, isNowEllipsed) }}
                        style={{ marginBottom: 4 }}
                    >
                        {change.changes}
                    </Paragraph>
                    {(isExpanded || isEllipsed) && (
                        <Button type="link" size="small" onClick={() => toggleExpanded(change.id)}>
                            {i18n.t(isExpanded ? "dashboard.showLess" : "dashboard.showMore")}
                        </Button>
                    )}
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            by {change.username} • {formatRelativeTime(change.changed)}
                        </Text>
                    </div>
                </div>
            ),
        };
    });

    return (
        <DashboardCardContainer
            title={i18n.t("dashboard.recentActivity")}
            extra={<SecondaryButton onClick={handleSeeAll}>{i18n.t("dashboard.seeAll")}</SecondaryButton>}
        >
            <Timeline items={timelineItems} />
        </DashboardCardContainer>
    );
};
